import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, ClipboardList } from "lucide-react";

export const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/teams").then((res) => setTeams(res.data)).catch(() => {});
  }, []);

  return (
    <section id="teams" data-testid="teams-section" className="relative py-24 lg:py-32 bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-[0.3em]">The Franchises</span>
          <h2 className="text-4xl sm:text-5xl uppercase tracking-tighter mt-4 leading-[0.95]">
            Meet The Teams
          </h2>
          <p className="mt-6 text-[#8A94A0] text-lg font-medium">
            The first squads stepping onto the diamond. Click any team to view its roster.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {teams.map((t, i) => (
            <motion.button
              key={t.slug}
              type="button"
              data-testid={`team-card-${t.slug}`}
              onClick={() => setSelected(t)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="group border border-white/10 hover:border-[#FF3B30] transition-colors overflow-hidden text-left"
            >
              <div className="aspect-square overflow-hidden bg-black relative">
                <img
                  src={t.logo}
                  alt={t.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/80 to-transparent">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white">
                    <ClipboardList size={14} /> View Roster
                  </span>
                </div>
              </div>
              <div className="p-5 bg-[#141414] flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">{t.city}</p>
                  <h3 className="text-xl uppercase tracking-tight mt-1 truncate">{t.name}</h3>
                  <div className="mt-3 flex items-center gap-4">
                    {[["W", t.wins], ["L", t.losses], ["T", t.ties]].map(([lbl, val]) => (
                      <div key={lbl} className="text-center">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0]">{lbl}</p>
                        <p className="font-heading text-2xl text-white leading-none mt-0.5">{val ?? 0}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <span className="text-[#8A94A0] group-hover:text-[#FF3B30] transition-colors text-xs font-bold uppercase tracking-wide shrink-0">
                  {t.roster?.length ? `${t.roster.length} players` : "Roster"}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-lg p-0 gap-0 rounded-none" data-testid="roster-modal">
          {selected && (
            <>
              <DialogTitle className="sr-only">{selected.name}</DialogTitle>
              <DialogDescription className="sr-only">Roster and record for {selected.name}</DialogDescription>
              <div className="flex items-center gap-4 p-6 border-b border-white/10">
                <img src={selected.logo} alt={selected.name} className="h-16 w-16 object-cover border border-white/10" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0]">{selected.city}</p>
                  <h3 className="text-2xl uppercase tracking-tight leading-none mt-1">{selected.name}</h3>
                  <div className="mt-3 flex items-center gap-5">
                    {[["W", selected.wins], ["L", selected.losses], ["T", selected.ties]].map(([lbl, val]) => (
                      <div key={lbl} className="text-center">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A94A0]">{lbl}</p>
                        <p className="font-heading text-xl text-[#FF3B30] leading-none mt-0.5">{val ?? 0}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6 max-h-[55vh] overflow-y-auto">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-4">
                  <Users size={14} /> Roster
                </p>
                {selected.roster?.length ? (
                  <ol className="space-y-2" data-testid="roster-list">
                    {selected.roster.map((p, idx) => (
                      <li key={idx} className="flex items-center gap-3 border-b border-white/5 pb-2">
                        <span className="font-heading text-lg text-white/30 w-8 shrink-0">{idx + 1}</span>
                        <span className="font-medium text-[#C7CDD4]">{p}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div data-testid="roster-empty" className="text-center py-10">
                    <ClipboardList className="mx-auto text-[#8A94A0] mb-4" size={40} strokeWidth={1.5} />
                    <p className="font-heading text-xl uppercase tracking-tight">Roster Coming Soon</p>
                    <p className="text-[#8A94A0] font-medium mt-2">This team's roster hasn't been announced yet. Check back soon. ⚾</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
